import apiClient, { safeJson } from '@/api/client';
import {
    LockerZoneResponse,
    LockerStatusResponse,
    LockerHistoryResponse,
    LockerCandidateResponse,
    LockerZone,
    LockerSlotResponse,
    LockerSlotMutationResponse,
    SetLockerSlotBody,
} from './types';

// ========================
// 1. 구역 (Locker Zones)
// ========================

export const getLockerZones = async ({ centerId }: { centerId: string }) => {
    const res = await apiClient.get(`v1/centers/${centerId}/locker-zones`);
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerZoneResponse | null };
};

export const createLockerZone = async ({
    centerId,
    body,
}: {
    centerId: string;
    body: {
        name: string;
        totalCount: number;
        startNumber?: number;
        displayOrder?: number;
    };
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/locker-zones`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as { success: boolean; zone: LockerZone } | null,
    };
};

export const updateLockerZone = async ({
    centerId,
    zoneId,
    body,
}: {
    centerId: string;
    zoneId: string;
    body: {
        name?: string;
        totalCount?: number;
        startNumber?: number;
        displayOrder?: number;
    };
}) => {
    const res = await apiClient.put(`v1/centers/${centerId}/locker-zones/${zoneId}`, { json: body });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as { success: boolean; zone: LockerZone } | null,
    };
};

export const deleteLockerZone = async ({ centerId, zoneId }: { centerId: string; zoneId: string }) => {
    const res = await apiClient.delete(`v1/centers/${centerId}/locker-zones/${zoneId}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as { success: boolean } | null };
};

// ========================
// 2. 관리 내역 (배정 현황 / 회수 기록)
// ========================

export const getLockerStatus = async ({ centerId, zoneId }: { centerId: string; zoneId: string }) => {
    const res = await apiClient.get(`v1/centers/${centerId}/locker-zones/${zoneId}/lockers`);
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerStatusResponse | null };
};

export const getLockerHistory = async ({
    centerId,
    zoneId,
    from,
    to,
    page = 1,
    limit = 20,
}: {
    centerId: string;
    zoneId?: string;
    from?: string; // ISO date string
    to?: string; // ISO date string
    page?: number;
    limit?: number;
}) => {
    const params = new URLSearchParams();
    if (zoneId) params.append('zoneId', zoneId);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const res = await apiClient.get(`v1/centers/${centerId}/lockers/history?${params.toString()}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerHistoryResponse | null };
};

export const getLockerCandidates = async ({ centerId, search, page = 1, limit = 50 }: { centerId: string; search?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const res = await apiClient.get(`v1/centers/${centerId}/lockers/assign-candidates?${params.toString()}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerCandidateResponse | null };
};

// ========================
// 3. 배정 및 회수 액션
// ========================

export const assignLocker = async ({
    centerId,
    subscriptionId,
    body,
}: {
    centerId: string;
    subscriptionId: string;
    /**
     * `lockNumber` 는 **선택이 아니다.**
     *
     * 키를 빼면 서버가 미전송을 "변경 없음"으로 읽어(facilitySubscription.service.ts:1671)
     * 기배정 이용권의 옛 개별값이 그대로 남는다. 값을 비울 때는 `null` 을 실어야 한다.
     * 옵셔널로 두면 `buildAssignLockNumberPayload` 가 보장하는 것이 이 경계에서 끊긴다.
     */
    body: { lockerZoneId: string; lockerNumber: string; lockNumber: string | null; force?: boolean };
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/assign-locker`, { json: body });
    const data = await safeJson(res);
    return { status: res.status, data: data as { success: boolean } | null };
};

export const releaseLocker = async ({ centerId, subscriptionId }: { centerId: string; subscriptionId: string }) => {
    const res = await apiClient.post(`v1/centers/${centerId}/subscriptions/${subscriptionId}/release-locker`);
    const data = await safeJson(res);
    return { status: res.status, data: data as { success: boolean } | null };
};

// ========================
// 4. 칸별 고정 비밀번호 (locker slot)
// ========================

export const getLockerSlot = async ({
    centerId,
    zoneId,
    lockerNumber,
}: {
    centerId: string;
    zoneId: string;
    lockerNumber: number | string;
}) => {
    const res = await apiClient.get(`v1/centers/${centerId}/locker-zones/${zoneId}/slots/${lockerNumber}`);
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerSlotResponse | null };
};

/**
 * 고정 비밀번호 설정/해제.
 *
 * ⚠️ **`defaultLockNumber` 는 키의 유무로 의미가 갈린다.**
 *   - 호출부가 키를 안 주면 → 본문에서도 빼서 "변경 없음"
 *   - 호출부가 `null`/`''` 을 주면 → `null` 로 실어 "해제"
 *
 * `{ ...body }` 로 그대로 넘기면 안 된다. 값이 `undefined` 인 키를 JSON.stringify 가 지워
 * "해제"가 조용히 "변경 없음"이 되기 때문이다. 그래서 hasOwnProperty 로 명시 판정한다.
 */
export const setLockerSlot = async ({
    centerId,
    zoneId,
    lockerNumber,
    body,
}: {
    centerId: string;
    zoneId: string;
    lockerNumber: number | string;
    body: SetLockerSlotBody;
}) => {
    const payload: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(body, 'defaultLockNumber')) {
        payload.defaultLockNumber = body.defaultLockNumber ?? null;
    }
    if (body.memo !== undefined) payload.memo = body.memo;
    if (body.unifyExisting === true) payload.unifyExisting = true;

    const res = await apiClient.put(`v1/centers/${centerId}/locker-zones/${zoneId}/slots/${lockerNumber}`, {
        json: payload,
    });
    const data = await safeJson(res);
    return { status: res.status, data: data as LockerSlotMutationResponse | null };
};

export const releaseExpiredLockers = async ({ centerId, zoneId }: { centerId: string; zoneId?: string }) => {
    const res = await apiClient.post(`v1/centers/${centerId}/lockers/release-expired`, {
        json: zoneId ? { zoneId } : {},
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as {
            success: boolean;
            matchedCount: number;
            modifiedCount: number;
        } | null,
    };
};
